// src/components/digital_twin/DrainageOutlookCard.js
// FlowSense MOW+ — live drainage outlook (next 24 h): the run-off model on the real Open-Meteo
// forecast, TessaDEM terrain, inspection clearance and live well levels. Server-cached and pushed on
// the websocket (`drainage_outlook`); this card only reads. "Run on twin" opens the full scenario.
import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import {
  Badge, Box, Button, Flex, HStack, Icon, IconButton, SimpleGrid, Skeleton, Text, Tooltip, VStack, useColorModeValue,
} from '@chakra-ui/react';
import { FaCloudRain, FaCrosshairs, FaSync, FaBrain } from 'react-icons/fa';
import webSocketService from '../../services/websocketService';
import { getDrainageOutlook } from '../../services/twinSimulationApi';

const SIGNAL = {
  none: { scheme: 'green', label: 'No ponding forecast' },
  advisory: { scheme: 'yellow', label: 'Drains near capacity' },
  watch: { scheme: 'orange', label: 'Ponding forecast' },
  warning: { scheme: 'red', label: 'Flooding forecast' },
};

const Stat = ({ label, value, color }) => {
  const muted = useColorModeValue('gray.500', 'gray.400');
  return (
    <Box>
      <Text fontSize="9px" fontWeight="800" color={muted} textTransform="uppercase">{label}</Text>
      <Text fontSize="md" fontWeight="900" fontFamily="mono" color={color}>{value ?? '—'}</Text>
    </Box>
  );
};

/** Tiny rain hyetograph with flooded-asset count (SVG, no chart lib). */
const RainBars = memo(({ timeline = [] }) => {
  const bar = useColorModeValue('#3182CE', '#63B3ED');
  const flood = '#E53E3E';
  if (!timeline.length) return null;
  const W = 300, H = 46;
  const maxR = Math.max(1, ...timeline.map((t) => t.rain_mm || 0));
  const maxF = Math.max(1, ...timeline.map((t) => t.assets_flooded || 0));
  const w = W / timeline.length;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} preserveAspectRatio="none" role="img" aria-label="Forecast rainfall per hour and assets ponding">
      {timeline.map((t, i) => (
        <rect key={t.hour} x={i * w + 0.5} width={Math.max(1, w - 1)} y={H - ((t.rain_mm || 0) / maxR) * (H - 4)} height={((t.rain_mm || 0) / maxR) * (H - 4)} fill={bar} opacity="0.75" />
      ))}
      <polyline fill="none" stroke={flood} strokeWidth="1.5" vectorEffect="non-scaling-stroke"
        points={timeline.map((t, i) => `${i * w + w / 2},${H - ((t.assets_flooded || 0) / maxF) * (H - 4)}`).join(' ')} />
    </svg>
  );
});
RainBars.displayName = 'RainBars';

function DrainageOutlookCard({ onRunScenario, onFocus }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const abortRef = useRef(null);
  const card = useColorModeValue('white', 'gray.800');
  const border = useColorModeValue('cyan.200', 'cyan.800');
  const muted = useColorModeValue('gray.500', 'gray.400');
  const rowHover = useColorModeValue('gray.50', 'whiteAlpha.100');

  const load = useCallback(async (force = false) => {
    abortRef.current?.abort();
    const ctrl = new AbortController(); abortRef.current = ctrl;
    setLoading(true);
    try {
      const body = await getDrainageOutlook({ force, signal: ctrl.signal });
      if (!ctrl.signal.aborted) { setData(body.data); setError(null); }
    } catch (e) {
      if (!ctrl.signal.aborted && e.name !== 'AbortError') setError(e.message);
    } finally {
      if (!ctrl.signal.aborted) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(false);
    const onPush = (msg) => { const p = msg?.payload ?? msg?.data ?? msg; if (p && p.summary) { setData(p); setError(null); } };
    const off = webSocketService.subscribe('drainage_outlook', onPush);
    return () => { abortRef.current?.abort(); if (typeof off === 'function') off(); else webSocketService.unsubscribe?.('drainage_outlook', onPush); };
  }, [load]);

  const s = data?.summary || {};
  const sig = SIGNAL[data?.signal] || SIGNAL.none;

  return (
    <Box bg={card} borderWidth="1px" borderColor={border} borderRadius="xl" p={4} position="relative" overflow="hidden" role="region" aria-label="Drainage outlook">
      <Box position="absolute" top={0} left={0} right={0} h="3px" bgGradient="linear(to-r, cyan.400, blue.500, purple.500)" />
      <Flex justify="space-between" align="center" mb={2}>
        <HStack spacing={2}>
          <Icon as={FaCloudRain} color="cyan.400" />
          <Text fontWeight="900" fontSize="sm">Drainage outlook · {data?.horizon_hours ?? 24} h</Text>
          <Badge colorScheme="purple" fontSize="8px"><Icon as={FaBrain} boxSize={2} mr={0.5} />AI</Badge>
        </HStack>
        <Tooltip label="Recompute now with the latest forecast" hasArrow>
          <IconButton size="xs" variant="ghost" icon={<FaSync />} aria-label="Refresh drainage outlook" isLoading={loading} onClick={() => load(true)} />
        </Tooltip>
      </Flex>

      {loading && !data ? <Skeleton h="110px" borderRadius="lg" /> : error && !data ? (
        <Text fontSize="xs" color="orange.400">{error}</Text>
      ) : data ? (
        <VStack align="stretch" spacing={2.5}>
          <HStack spacing={2} flexWrap="wrap">
            <Badge colorScheme={sig.scheme} variant="solid" borderRadius="full" px={2}>{sig.label}</Badge>
            <Text fontSize="10px" color={muted}>{data.rain?.total_mm ?? 0} mm forecast · peak {data.rain?.peak_mm_hr ?? 0} mm/h · {data.rain?.source}</Text>
          </HStack>
          <SimpleGrid columns={4} spacing={2}>
            <Stat label="Ponding" value={s.assetsFlooded ?? 0} color={s.assetsFlooded ? 'blue.400' : undefined} />
            <Stat label="Over cap." value={s.overCapacity ?? 0} color={s.overCapacity ? 'orange.400' : undefined} />
            <Stat label="First" value={s.earliestFloodHours != null ? `+${s.earliestFloodHours}h` : '—'} />
            <Stat label="Max depth" value={s.maxFloodDepthM ? `${s.maxFloodDepthM} m` : '—'} />
          </SimpleGrid>
          <RainBars timeline={data.timeline} />
          {(data.at_risk || []).slice(0, 5).map((n) => (
            <Flex key={n.asset_id} as="button" type="button" textAlign="left" gap={2} align="center" px={1.5} py={1} borderRadius="md" _hover={{ bg: rowHover }} onClick={() => onFocus?.(n)}>
              <Box w="6px" h="24px" borderRadius="full" bg={n.state === 'critical' ? 'red.400' : 'orange.400'} flexShrink={0} />
              <Box flex={1} minW={0}>
                <Text fontSize="11px" fontWeight="700" noOfLines={1}>{n.name || n.asset_id}</Text>
                <Text fontSize="9px" color={muted} noOfLines={1}>
                  {n.collector_kind?.replace('_', ' ')}{n.peak_flood_depth_m ? ` · ${n.peak_flood_depth_m} m` : ''}{n.time_to_flood_hours != null ? ` from +${n.time_to_flood_hours}h` : ''}
                  {n.live_level_pct != null ? ` · now ${n.live_level_pct}% full` : ''}{n.clearance_pct != null && n.clearance_pct < 60 ? ` · silt ${n.clearance_pct}%` : ''}
                </Text>
              </Box>
              <Icon as={FaCrosshairs} boxSize={2.5} color={muted} />
            </Flex>
          ))}
          {!data.at_risk?.length && <Text fontSize="xs" color={muted}>Every well and catch basin keeps up with the forecast rain.</Text>}
          <HStack justify="space-between">
            <Text fontSize="9px" color={muted}>
              Evidence: terrain {data.evidence?.terrain_pct ?? 0}% · {data.evidence?.clearance_scores ?? 0} inspections · {data.evidence?.live_levels ?? 0} live levels
            </Text>
            {onRunScenario && <Button size="xs" colorScheme="cyan" onClick={onRunScenario}>Run on twin</Button>}
          </HStack>
          <Text fontSize="9px" color={muted}>Updated {data.generated_at ? new Date(data.generated_at).toLocaleTimeString() : '—'}</Text>
        </VStack>
      ) : null}
    </Box>
  );
}

export default memo(DrainageOutlookCard);
